const STORAGE_KEY = 'goettling-session-tracking';
const COOKIE_NAME = 'goettling_attrib';
const COOKIE_DAYS = 90;
const FIRST_TOUCH_KEYS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term'
]);
const TRACKING_PARAM_KEYS = new Set([
  'view',
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'gbraid',
  'wbraid',
  'mc_cid',
  'mc_eid'
]);
const WA_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const LINK_SELECTOR = 'a[href]';
const FORM_SELECTOR = '.contact-form__form, form.js-goettling-contact';
const FIELD_PREFIX = 'contact[';
const FIELD_SUFFIX = ']';
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isTrackedParamKey(key) {
  return TRACKING_PARAM_KEYS.has(key) || key.startsWith('utm_');
}

function uuid() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = (Math.random() * 16) | 0;
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}

function readNamedCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  if (!match) return '';
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function readCookie() {
  try {
    const raw = readNamedCookie(COOKIE_NAME);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function cookieFirstSegment(name) {
  const raw = readNamedCookie(name);
  if (!raw) return '';
  // Clarity _clck/_clsk: id|… or id^… (sometimes ;). First segment only.
  return raw.split(/[|^;]/)[0] || raw;
}

function gaClientId() {
  const raw = readNamedCookie('_ga');
  const parts = raw.split('.');
  if (parts.length >= 4) return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  return '';
}

function platformIds() {
  return {
    ga_client_id: gaClientId(),
    clarity_user: cookieFirstSegment('_clck'),
    clarity_session: cookieFirstSegment('_clsk')
  };
}

function tagClarity(vid) {
  if (!vid) return;

  function send() {
    if (typeof window.clarity !== 'function') return false;
    // Stub still has .q until consent loads full tracker — queue anyway.
    try {
      window.clarity('identify', vid, undefined, undefined, vid);
      window.clarity('set', 'visitor_id', vid);
      return true;
    } catch {
      return false;
    }
  }

  if (send()) return;

  let tries = 0;
  const timer = window.setInterval(() => {
    tries += 1;
    if (send() || tries > 100) window.clearInterval(timer);
  }, 300);

  // Re-send after consent / late Clarity load (common on this site).
  window.setTimeout(send, 3000);
  window.setTimeout(send, 8000);
  document.addEventListener('visibilitychange', function onVis() {
    if (document.visibilityState === 'visible') send();
  });
}

function writeCookie(data) {
  try {
    const maxAge = COOKIE_DAYS * 24 * 60 * 60;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(data))}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
  } catch {
    // Ignore cookie write failures.
  }
}

function readStoredState() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null') || {};
  } catch {
    return {};
  }
}

function writeStoredState(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage failures in private browsing or restricted contexts.
  }
}

function collectTrackingParams(searchParams) {
  const params = {};
  for (const [key, value] of searchParams.entries()) {
    if (!value || !isTrackedParamKey(key)) continue;
    params[key] = value;
  }
  return params;
}

function mergeFirstTouch(existing, incoming) {
  const merged = { ...(existing || {}) };
  for (const [key, value] of Object.entries(incoming || {})) {
    if (!value) continue;
    if (FIRST_TOUCH_KEYS.has(key) && merged[key]) continue;
    merged[key] = value;
  }
  return merged;
}

/** Never let an empty {} beat a filled params object. */
function pickParams(...sources) {
  let out = {};
  for (const src of sources) {
    if (!src || typeof src !== 'object') continue;
    out = mergeFirstTouch(out, src);
  }
  return out;
}

function pickVid(...candidates) {
  for (const value of candidates) {
    if (value && UUID_RE.test(String(value))) return String(value);
  }
  return '';
}

function cookiePayload(state) {
  return {
    vid: state.vid,
    created: state.created,
    params: state.params || {},
    entryUrl: state.entryUrl || '',
    entryPath: state.entryPath || '',
    initialReferrer: state.initialReferrer || ''
  };
}

function buildSessionState() {
  const cookieState = readCookie() || {};
  const storedState = readStoredState();
  const currentUrl = new URL(window.location.href);
  const urlVid = currentUrl.searchParams.get('vid');
  const currentParams = collectTrackingParams(currentUrl.searchParams);
  const mergedParams = pickParams(cookieState.params, storedState.params, currentParams);
  const vid =
    pickVid(cookieState.vid, storedState.vid, urlVid) || uuid();
  const created = cookieState.created || storedState.created || new Date().toISOString();
  const entryUrl = cookieState.entryUrl || storedState.entryUrl || window.location.href;
  const entryPath =
    cookieState.entryPath ||
    storedState.entryPath ||
    `${currentUrl.pathname}${currentUrl.search}`;
  const initialReferrer =
    cookieState.initialReferrer || storedState.initialReferrer || document.referrer || '';

  return {
    vid,
    created,
    params: mergedParams,
    entryUrl,
    entryPath,
    initialReferrer,
    lastPageUrl: window.location.href
  };
}

function buildWhatsAppLandingUrl(state) {
  const query = new URLSearchParams();
  if (state.vid) query.set('vid', state.vid);
  const fromState = state.params || {};
  for (const key of WA_UTM_KEYS) {
    if (fromState[key]) query.set(key, fromState[key]);
  }
  const cookie = readCookie() || {};
  if (!query.has('vid') && cookie.vid) query.set('vid', cookie.vid);
  const cp = cookie.params || {};
  for (const key of WA_UTM_KEYS) {
    if (!query.get(key) && cp[key]) query.set(key, cp[key]);
  }
  const qs = query.toString();
  return `https://goettling.ae/${qs ? `?${qs}` : ''}`;
}

function buildWhatsAppText(state) {
  return `Hello, I came from ${buildWhatsAppLandingUrl(state)}`;
}

function isWhatsAppHref(href) {
  if (!href) return false;
  return /wa\.me\//i.test(href) || /api\.whatsapp\.com\//i.test(href) || /whatsapp\.com\/send/i.test(href);
}

function withWhatsAppText(href, text) {
  let phone = '';
  try {
    const url = new URL(href, window.location.origin);
    phone = url.searchParams.get('phone') || '';
    if (!phone) {
      const digits = url.pathname.match(/\/(\d{8,15})(?:\/|$)/);
      if (digits) phone = digits[1];
    }
  } catch {
    return href;
  }
  if (!phone) return href;
  let next = `https://api.whatsapp.com/send?phone=${phone}`;
  if (text) next += `&text=${encodeURIComponent(text)}`;
  return next;
}

function updateWhatsAppLinks(state, root = document) {
  const text = buildWhatsAppText(state);
  window.GoettlingAttrib = window.GoettlingAttrib || {};
  window.GoettlingAttrib.waText = text;

  const links = [];
  if (root instanceof HTMLAnchorElement) links.push(root);
  if (root && root.querySelectorAll) {
    for (const link of root.querySelectorAll('a[href]')) links.push(link);
  }

  for (const link of links) {
    if (!(link instanceof HTMLAnchorElement)) continue;
    if (link.hasAttribute('data-no-session-tracking')) continue;
    const href = link.getAttribute('href');
    if (!isWhatsAppHref(href)) continue;
    link.href = withWhatsAppText(href, text);
  }
}

function persistState(state) {
  writeStoredState(state);
  writeCookie(cookiePayload(state));
  window.GoettlingAttrib = {
    vid: state.vid,
    params: state.params || {},
    entryUrl: state.entryUrl || '',
    entryPath: state.entryPath || '',
    initialReferrer: state.initialReferrer || '',
    waText: buildWhatsAppText(state)
  };
  pushAttribToDataLayer(state);
}

/** Expose stitch fields for GTM DLVs on Lead / whatsapp_click (no event name — sticky vars). */
function pushAttribToDataLayer(state) {
  try {
    window.dataLayer = window.dataLayer || [];
    const params = state.params || {};
    window.dataLayer.push({
      visitor_id: state.vid || '',
      lead_source: params.utm_source || '',
      lead_medium: params.utm_medium || '',
      lead_campaign: params.utm_campaign || '',
      lead_content: params.utm_content || '',
      lead_term: params.utm_term || ''
    });
  } catch {
    // ignore
  }
}

function appendTrackingParams(urlLike, params) {
  try {
    const url = new URL(urlLike, window.location.origin);
    if (!/^https?:$/.test(url.protocol)) return null;
    if (url.origin !== window.location.origin) return null;
    for (const [key, value] of Object.entries(params || {})) {
      if (!value || url.searchParams.has(key)) continue;
      url.searchParams.set(key, value);
    }
    return url;
  } catch {
    return null;
  }
}

function refreshPageUrl(state) {
  const nextUrl = appendTrackingParams(window.location.href, state.params);
  if (!nextUrl) return;
  if (nextUrl.href === window.location.href) return;
  window.history.replaceState(window.history.state, '', nextUrl);
}

function updateTrackedLinks(state, root = document) {
  if (!state.params || Object.keys(state.params).length === 0) return;

  for (const link of root.querySelectorAll(LINK_SELECTOR)) {
    if (!(link instanceof HTMLAnchorElement)) continue;
    if (link.hasAttribute('data-no-session-tracking')) continue;
    if (link.hasAttribute('download')) continue;

    const href = link.getAttribute('href');
    if (
      !href ||
      href.startsWith('#') ||
      href.startsWith('mailto:') ||
      href.startsWith('tel:') ||
      href.startsWith('javascript:')
    ) {
      continue;
    }

    const nextUrl = appendTrackingParams(href, state.params);
    if (!nextUrl) continue;
    link.href = nextUrl.toString();
  }
}

function createHiddenInput(name, value) {
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = `${FIELD_PREFIX}${name}${FIELD_SUFFIX}`;
  input.value = value;
  input.dataset.sessionTrackingInput = 'true';
  return input;
}

function attribValue(state, field) {
  const platform = platformIds();
  if (field === 'visitor_id') return state.vid || '';
  if (field === 'landing_page') return state.entryUrl || '';
  if (field === 'landing_path') return state.entryPath || '';
  if (field === 'current_page') return window.location.href;
  if (field === 'initial_referrer') return state.initialReferrer || '';
  if (field === 'ga_client_id') return platform.ga_client_id;
  if (field === 'clarity_user') return platform.clarity_user;
  if (field === 'clarity_session') return platform.clarity_session;
  return (state.params && state.params[field]) || '';
}

function updateContactForms(state) {
  const forms = document.querySelectorAll(FORM_SELECTOR);
  if (!forms.length) return;

  const fallbackFields = {
    visitor_id: state.vid,
    ...state.params,
    ...platformIds(),
    landing_page: state.entryUrl,
    landing_path: state.entryPath,
    current_page: window.location.href,
    initial_referrer: state.initialReferrer
  };

  for (const form of forms) {
    const declared = form.querySelectorAll('[data-attrib-field]');

    if (declared.length) {
      for (const input of declared) {
        const value = attribValue(state, input.getAttribute('data-attrib-field'));
        if (value) input.value = value;
      }
      continue;
    }

    for (const existingInput of form.querySelectorAll('[data-session-tracking-input="true"]')) {
      existingInput.remove();
    }

    for (const [key, value] of Object.entries(fallbackFields)) {
      if (!value) continue;
      form.append(createHiddenInput(key, value));
    }
  }
}

function observeDomChanges(state) {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches?.(LINK_SELECTOR)) {
          updateTrackedLinks(state, node.parentElement || document);
          updateWhatsAppLinks(state, node.parentElement || document);
          continue;
        }

        updateTrackedLinks(state, node);
        updateWhatsAppLinks(state, node);

        if (node.matches?.(FORM_SELECTOR) || node.querySelector?.(FORM_SELECTOR)) {
          updateContactForms(state);
        }
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

function initSessionTracking() {
  try {
    const state = buildSessionState();
    persistState(state);
    refreshPageUrl(state);
    updateTrackedLinks(state);
    updateWhatsAppLinks(state);
    updateContactForms(state);
    observeDomChanges(state);
    tagClarity(state.vid);
    window.setTimeout(() => {
      try {
        updateContactForms(state);
        updateWhatsAppLinks(state);
      } catch {
        // ignore late refresh failures
      }
    }, 2500);
  } catch (err) {
    // Never leave the page without a stitch key if possible.
    try {
      const vid = uuid();
      const fallback = {
        vid,
        created: new Date().toISOString(),
        params: collectTrackingParams(new URL(window.location.href).searchParams),
        entryUrl: window.location.href,
        entryPath: `${window.location.pathname}${window.location.search}`,
        initialReferrer: document.referrer || ''
      };
      persistState(fallback);
      updateContactForms(fallback);
      updateWhatsAppLinks(fallback);
      tagClarity(vid);
    } catch {
      // give up silently
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSessionTracking, { once: true });
} else {
  initSessionTracking();
}
